import { ThemeButton } from './ThemeWrapper';
import { discoveryPercent, formatElapsed } from '../domain/infra/discoveryProgress';
import { countOrDash, formatSessionUsers, formatSshPolicy } from '../domain/infra/userInventory';

function sshLabel(host) {
  const status = String(host.ssh || '').toUpperCase();
  if (status === 'SKIPPED' || status === 'OK' || status === 'FAILED') return status;
  return '—';
}

function InventoryException({ exception, elapsedMs }) {
  if (!exception?.message) return null;
  return (
    <div
      className="rounded p-3 space-y-1 text-sm"
      style={{ border: '1px solid var(--color-error, #b91c1c)', backgroundColor: 'rgba(185, 28, 28, 0.06)' }}
      role="alert"
    >
      <p className="font-medium text-red-700">User inventory failed</p>
      {exception.code ? (
        <p className="text-red-700">
          Code: <code className="font-mono">{exception.code}</code>
        </p>
      ) : null}
      <p className="text-red-700 whitespace-pre-wrap">{exception.message}</p>
      {elapsedMs >= 1000 ? (
        <p className="text-red-700">Stopped after {formatElapsed(elapsedMs)}</p>
      ) : null}
      {exception.sshUser ? (
        <p className="text-red-700">
          SSH user: <code className="font-mono">{exception.sshUser}</code>
        </p>
      ) : null}
      {exception.keyPath ? (
        <p className="text-red-700">
          Key path: <code className="font-mono">{exception.keyPath}</code>
        </p>
      ) : null}
    </div>
  );
}

function InventoryProgress({ running, elapsedMs, progress }) {
  if (!running && progress?.status !== 'running') return null;
  const total = Number(progress?.total || 0);
  const probed = Number(progress?.probed || 0);
  const percent = total ? discoveryPercent({ probed, total }) : 0;
  const elapsed = formatElapsed(progress?.elapsedMs || elapsedMs);

  return (
    <div
      className="p-3 rounded-lg space-y-2"
      style={{ border: '1px solid var(--color-border)' }}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="theme-text font-medium">Running user inventory</span>
        <span className="theme-text-secondary font-mono text-xs">
          {elapsed}
          {total ? ` · ${probed}/${total}` : ''}
          {total ? ` · ${percent}%` : ''}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded" style={{ backgroundColor: 'var(--color-border)' }}>
        <div
          className="h-full rounded transition-[width] duration-300 ease-out"
          style={{
            width: `${total ? percent : Math.min(95, 8 + (Number(elapsedMs) || 0) / 800)}%`,
            backgroundColor: 'var(--color-primary, #1f4e79)',
          }}
        />
      </div>
      <p className="text-xs theme-text-secondary">
        {progress?.currentAlias
          ? `Counting sessions on ${progress.currentAlias}. If this host hangs, SMS skips it and verifies the next server.`
          : 'Counting login sessions and accounts on catalog hosts. A hung host is skipped so the next server can be verified.'}
      </p>
    </div>
  );
}

function InventoryResult({ progress, runMessage, elapsedMs }) {
  const result = progress?.result;
  const hostErrors = result?.hostErrors || [];
  if (!runMessage && !result && !hostErrors.length) return null;
  const elapsed = formatElapsed(progress?.elapsedMs || elapsedMs);
  return (
    <div className="space-y-2">
      {runMessage ? <p className="text-sm theme-text">{runMessage}</p> : null}
      {result ? (
        <p className="text-sm theme-text-secondary">
          Last run captured in {elapsed}: {result.sshOk} SSH OK, {result.sshFailed} failed
          {result.sessions != null ? ` · ${result.sessions} sessions` : ''}
          {result.vncProcs != null ? ` · ${result.vncProcs} VNC processes` : ''}
          {result.inventoriedAt ? ` · ${new Date(result.inventoriedAt).toLocaleString()}` : ''}
        </p>
      ) : null}
      {hostErrors.length ? (
        <div
          className="rounded p-3 text-sm space-y-1"
          style={{ border: '1px solid var(--color-error, #b91c1c)', backgroundColor: 'rgba(185, 28, 28, 0.06)' }}
          role="alert"
        >
          <p className="font-medium text-red-700">Host exceptions ({hostErrors.length})</p>
          <ul className="list-disc pl-5 space-y-1">
            {hostErrors.map((item) => (
              <li key={item.alias} className="text-red-700">
                <code className="font-mono">{item.alias}</code>
                {item.ssh && item.ssh !== 'FAILED' ? ` [${item.ssh}]` : ''}: {item.error}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function UserInventoryTable({ hosts }) {
  if (!hosts?.length) return null;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm theme-text">
        <thead>
          <tr className="text-left theme-text-secondary">
            <th className="pr-4 py-1">Host</th>
            <th className="pr-4 py-1">SSH</th>
            <th className="pr-4 py-1">Sessions</th>
            <th className="pr-4 py-1">VNC</th>
            <th className="pr-4 py-1">Login shells</th>
            <th className="pr-4 py-1">NSS / local</th>
            <th className="pr-4 py-1">Logged-in users</th>
            <th className="pr-4 py-1">SSH policy</th>
            <th className="pr-4 py-1">Exception</th>
          </tr>
        </thead>
        <tbody>
          {hosts.map((host) => (
            <tr key={`${host.alias}-${host.host}`}>
              <td className="pr-4 py-2 align-top">
                <div>{host.alias}</div>
                <div className="text-xs theme-text-secondary font-normal">{host.fqdn || host.host || '—'}</div>
              </td>
              <td className="pr-4 py-2 align-top">{sshLabel(host)}</td>
              <td className="pr-4 py-2 align-top">{countOrDash(host.sessions)}</td>
              <td className="pr-4 py-2 align-top">{countOrDash(host.vncProcs)}</td>
              <td className="pr-4 py-2 align-top">{countOrDash(host.usersLogin)}</td>
              <td className="pr-4 py-2 align-top">
                {countOrDash(host.usersNss)} / {countOrDash(host.usersLocal)}
              </td>
              <td className="pr-4 py-2 align-top">{formatSessionUsers(host.sessionUsers) || '—'}</td>
              <td className="pr-4 py-2 align-top">{formatSshPolicy(host) || '—'}</td>
              <td className="pr-4 py-2 align-top text-red-700">{host.error || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function LabUserInventoryPanel({
  inventory,
  loading,
  error,
  running,
  runError,
  runMessage,
  onRunInventory,
  progress,
  exception,
  elapsedMs,
  extraBusy = false,
}) {
  const hosts = inventory?.hosts || [];
  const busy = Boolean(running || extraBusy);

  return (
    <section className="rounded border p-4 space-y-3" style={{ borderColor: 'var(--color-border)' }}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold theme-text">User inventory</h2>
          <p className="text-sm theme-text-secondary">
            Counts login sessions, VNC processes, and account totals on the same catalog hosts as
            discovery. SMS does not collect password hashes, keys, sudo listings, or login history.
            If a host is still running past 12s, SMS skips it and verifies the next server.
          </p>
        </div>
        {onRunInventory ? (
          <ThemeButton type="button" disabled={busy} onClick={onRunInventory}>
            {running ? `Running… ${formatElapsed(elapsedMs)}` : 'Run user inventory'}
          </ThemeButton>
        ) : null}
      </div>

      <InventoryProgress running={running} elapsedMs={elapsedMs} progress={progress} />
      <InventoryException exception={exception} elapsedMs={elapsedMs} />
      {!exception && runError ? <p className="text-sm text-red-600 whitespace-pre-wrap">{runError}</p> : null}
      <InventoryResult progress={progress} runMessage={exception ? '' : runMessage} elapsedMs={elapsedMs} />

      {loading ? <p className="text-sm theme-text-secondary">Loading user inventory…</p> : null}
      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      {inventory?.inventoriedAt && !loading ? (
        <p className="text-sm theme-text-secondary">
          Last user inventory: {new Date(inventory.inventoriedAt).toLocaleString()}
        </p>
      ) : null}

      {hosts.length ? <UserInventoryTable hosts={hosts} /> : null}
    </section>
  );
}
